import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ClipboardCheck, Save, Search, Undo2, UserCog } from 'lucide-react';
import { adminAssessment } from '@/api/admin';
import type { AssessmentConfig as AC, UserAssessmentConfig } from '@/api/extras';
import type { Money } from '@/api/types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Pill } from '@/components/widgets/Pill';
import { toast } from '@/components/ui/sonner';
import { ApiError } from '@/lib/http';
import { fmtMoney, moneyToNumber } from '@/lib/format';

/** 美元(小数)→ Money{value=分, digits:2}。空 / 非法 → 0。 */
function dollarsToMoney(v: string | number): Money {
  const n = typeof v === 'number' ? v : Number(v);
  return { value: Number.isFinite(n) ? Math.round(n * 100) : 0, digits: 2 };
}

/** Money → 美元字符串(供金额 Input 编辑;空/0 也显示数字)。 */
function moneyToDollarStr(m: Money | null | undefined): string {
  return String(moneyToNumber(m));
}

export function AssessmentConfig() {
  const qc = useQueryClient();
  const cfgQ = useQuery({ queryKey: ['admin', 'assessment-config'], queryFn: adminAssessment.getConfig });

  const [draft, setDraft] = useState<AC | null>(null);

  useEffect(() => {
    if (cfgQ.data) setDraft(cfgQ.data);
  }, [cfgQ.data]);

  const dirty = useMemo(() => {
    if (!draft || !cfgQ.data) return false;
    return JSON.stringify(draft) !== JSON.stringify(cfgQ.data);
  }, [draft, cfgQ.data]);

  const submit = useMutation({
    mutationFn: () => {
      if (!draft) throw new Error('no draft');
      return adminAssessment.updateConfig(draft);
    },
    onSuccess: () => {
      toast.success('已保存', { description: '全局默认考核配置已即时生效' });
      qc.invalidateQueries({ queryKey: ['admin', 'assessment-config'] });
      // 用户覆盖卡片对照的 global 也变了,顺带刷新已加载的用户配置
      qc.invalidateQueries({ queryKey: ['admin', 'assessment-user-config'] });
    },
    onError: (err) => toast.error('保存失败', { description: err instanceof ApiError ? err.message : '' }),
  });

  if (!draft) return null;

  return (
    <div className="p-6 space-y-4">
      <header>
        <h1 className="text-xl font-bold text-ink-0 flex items-center gap-2">
          <ClipboardCheck className="h-5 w-5 text-brand" />
          考核设置
        </h1>
        <p className="text-sm text-ink-2 mt-1">
          逐用户每交易日判定 · 启用的维度【全部】达标才算当天达标 · 全局默认 + 可按用户覆盖
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>全局默认</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-0.5">
              <Label htmlFor="assess-enabled">启用考核</Label>
              <p className="text-[11px] text-ink-3 leading-relaxed">
                总开关。关闭后所有用户不参与考核判定。
              </p>
            </div>
            <Switch
              id="assess-enabled"
              checked={draft.enabled}
              onCheckedChange={(v) => setDraft({ ...draft, enabled: v })}
            />
          </div>

          <DimensionEditor value={draft} disabled={!draft.enabled} onChange={setDraft} />

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-line">
            <Button variant="outline" disabled={!dirty || submit.isPending} onClick={() => cfgQ.data && setDraft(cfgQ.data)}>
              重置
            </Button>
            <Button onClick={() => submit.mutate()} disabled={!dirty || submit.isPending}>
              <Save className="h-4 w-4" />
              {submit.isPending ? '保存中...' : '保存'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {cfgQ.data && <UserOverrideCard global={cfgQ.data} />}
    </div>
  );
}

/**
 * 三维度可编辑表单(全局默认与用户覆盖共用)。
 * - 每日交易笔数:Switch + 整数 Input(目标笔数);
 * - 每日盈利金额:Switch + 金额 Input(美元,保存转 Money);
 * - 每日亏损上限:Switch + 金额 Input(美元,正数=上限)。
 * 各维度自身开关关闭时其输入禁用;disabled(总开关关)时整段禁用。
 */
function DimensionEditor({
  value,
  disabled,
  onChange,
}: {
  value: AC;
  disabled?: boolean;
  onChange: (next: AC) => void;
}) {
  return (
    <div className="space-y-5">
      {/* 维度① 每日交易笔数 */}
      <div className="space-y-2">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-0.5">
            <Label htmlFor={`dim-trade-${value.tradeCountEnabled}`}>每日交易笔数</Label>
            <p className="text-[11px] text-ink-3 leading-relaxed">当日开仓笔数 ≥ 目标即达标。</p>
          </div>
          <Switch
            checked={value.tradeCountEnabled}
            disabled={disabled}
            onCheckedChange={(v) => onChange({ ...value, tradeCountEnabled: v })}
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-ink-2">目标笔数</Label>
          <Input
            type="number"
            min={0}
            step="1"
            className="w-48 num"
            value={value.tradeCountTarget}
            disabled={disabled || !value.tradeCountEnabled}
            onChange={(e) => onChange({ ...value, tradeCountTarget: Math.max(0, Math.floor(Number(e.target.value) || 0)) })}
          />
        </div>
      </div>

      {/* 维度② 每日盈利金额 */}
      <div className="space-y-2 pt-4 border-t border-line">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-0.5">
            <Label>每日盈利金额</Label>
            <p className="text-[11px] text-ink-3 leading-relaxed">当日已实现盈亏 ≥ 目标即达标。</p>
          </div>
          <Switch
            checked={value.profitEnabled}
            disabled={disabled}
            onCheckedChange={(v) => onChange({ ...value, profitEnabled: v })}
          />
        </div>
        <MoneyInput
          label="目标盈利($)"
          value={value.profitTarget}
          disabled={disabled || !value.profitEnabled}
          onChange={(m) => onChange({ ...value, profitTarget: m })}
        />
      </div>

      {/* 维度③ 每日亏损上限 */}
      <div className="space-y-2 pt-4 border-t border-line">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-0.5">
            <Label>每日亏损金额</Label>
            <p className="text-[11px] text-ink-3 leading-relaxed">当日已实现亏损幅度 ≤ 上限即达标(正数)。</p>
          </div>
          <Switch
            checked={value.lossEnabled}
            disabled={disabled}
            onCheckedChange={(v) => onChange({ ...value, lossEnabled: v })}
          />
        </div>
        <MoneyInput
          label="亏损上限($,正数)"
          value={value.lossCap}
          disabled={disabled || !value.lossEnabled}
          onChange={(m) => onChange({ ...value, lossCap: m })}
        />
      </div>
    </div>
  );
}

/** 金额输入(美元小数)— 保存时转 Money;内部用受控字符串避免输入抖动。 */
function MoneyInput({
  label,
  value,
  disabled,
  onChange,
}: {
  label: string;
  value: Money;
  disabled?: boolean;
  onChange: (m: Money) => void;
}) {
  // 用本地字符串维护编辑态,外部 value 变化时同步(例如重置 / 加载用户配置)。
  const [text, setText] = useState<string>(moneyToDollarStr(value));
  useEffect(() => {
    setText(moneyToDollarStr(value));
  }, [value]);
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-ink-2">{label}</Label>
      <div className="relative w-48">
        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-3 text-sm">$</span>
        <Input
          type="number"
          min={0}
          step="0.01"
          className="pl-6 num"
          value={text}
          disabled={disabled}
          onChange={(e) => {
            setText(e.target.value);
            onChange(dollarsToMoney(e.target.value));
          }}
        />
      </div>
    </div>
  );
}

/**
 * 按用户覆盖:输入 userId →「加载」拉取该用户配置;展示 hasOverride、effective 与 global 对照;
 * 可编辑表单(默认用 effective 预填);保存覆盖 / 恢复默认。
 */
function UserOverrideCard({ global }: { global: AC }) {
  const qc = useQueryClient();
  const [userIdInput, setUserIdInput] = useState('');
  const [loadedUserId, setLoadedUserId] = useState<string | null>(null);
  const [draft, setDraft] = useState<AC | null>(null);

  const userQ = useQuery({
    queryKey: ['admin', 'assessment-user-config', loadedUserId],
    queryFn: () => adminAssessment.getUserConfig(loadedUserId!),
    enabled: !!loadedUserId,
  });

  useEffect(() => {
    if (userQ.data) setDraft(userQ.data.effective);
    else if (userQ.isError) setDraft(null); // 加载失败复位,避免残留上个用户的脏 draft
  }, [userQ.data, userQ.isError]);

  const save = useMutation({
    mutationFn: () => {
      if (!loadedUserId || !draft) throw new Error('no draft');
      return adminAssessment.setUserConfig(loadedUserId, draft);
    },
    onSuccess: () => {
      toast.success('已保存用户覆盖');
      qc.invalidateQueries({ queryKey: ['admin', 'assessment-user-config', loadedUserId] });
    },
    onError: (e) => toast.error('保存失败', { description: e instanceof ApiError ? e.message : '' }),
  });

  const clear = useMutation({
    mutationFn: () => {
      if (!loadedUserId) throw new Error('no user');
      return adminAssessment.clearUserConfig(loadedUserId);
    },
    onSuccess: () => {
      toast.success('已恢复全局默认');
      qc.invalidateQueries({ queryKey: ['admin', 'assessment-user-config', loadedUserId] });
    },
    onError: (e) => toast.error('恢复失败', { description: e instanceof ApiError ? e.message : '' }),
  });

  const onLoad = () => {
    const id = userIdInput.trim();
    if (!id) {
      toast.error('请输入用户 ID');
      return;
    }
    setLoadedUserId(id);
  };

  const cfg: UserAssessmentConfig | undefined = userQ.data;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <UserCog className="h-5 w-5 text-brand" />
          按用户覆盖
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex items-end gap-2">
          <div className="space-y-1.5">
            <Label htmlFor="assess-user-id">用户 ID</Label>
            <div className="relative w-56">
              <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-ink-3" />
              <Input
                id="assess-user-id"
                className="pl-7 num"
                placeholder="输入用户 ID"
                value={userIdInput}
                onChange={(e) => setUserIdInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onLoad()}
              />
            </div>
          </div>
          <Button variant="outline" onClick={onLoad} disabled={userQ.isFetching}>
            {userQ.isFetching ? '加载中...' : '加载'}
          </Button>
        </div>

        {userQ.isError && (
          <div className="text-sm text-down-dim">
            加载失败:{userQ.error instanceof ApiError ? userQ.error.message : '未知错误'}
          </div>
        )}

        {cfg && draft && (
          <>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="text-ink-2">{cfg.phone || cfg.username || cfg.userId}</span>
              {cfg.hasOverride ? (
                <Pill tone="brand">已覆盖</Pill>
              ) : (
                <Pill tone="neutral">使用全局默认</Pill>
              )}
            </div>

            {/* effective ↔ global 对照(只读) */}
            <ConfigCompare effective={cfg.effective} global={cfg.global} />

            <div className="pt-2 border-t border-line">
              <div className="mb-3 flex items-center justify-between">
                <div className="text-xs font-semibold text-ink-2">该用户覆盖配置</div>
                <Switch
                  checked={draft.enabled}
                  onCheckedChange={(v) => setDraft({ ...draft, enabled: v })}
                />
              </div>
              <DimensionEditor value={draft} disabled={!draft.enabled} onChange={setDraft} />
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-line">
              <Button
                variant="outline"
                disabled={!cfg.hasOverride || clear.isPending}
                title={cfg.hasOverride ? '清除覆盖,恢复全局默认' : '该用户当前未覆盖'}
                onClick={() => clear.mutate()}
              >
                <Undo2 className="h-4 w-4" />
                {clear.isPending ? '恢复中...' : '恢复默认'}
              </Button>
              <Button onClick={() => save.mutate()} disabled={save.isPending}>
                <Save className="h-4 w-4" />
                {save.isPending ? '保存中...' : '保存覆盖'}
              </Button>
            </div>
          </>
        )}

        {!cfg && !userQ.isError && (
          <p className="text-sm text-ink-3">
            输入用户 ID 并「加载」以查看 / 编辑其考核覆盖。当前全局默认:{describeConfig(global)}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

/** effective ↔ global 只读对照表。 */
function ConfigCompare({ effective, global }: { effective: AC; global: AC }) {
  const rows: Array<[string, string, string]> = [
    ['启用考核', effective.enabled ? '开' : '关', global.enabled ? '开' : '关'],
    [
      '每日交易笔数',
      effective.tradeCountEnabled ? `${effective.tradeCountTarget} 笔` : '关',
      global.tradeCountEnabled ? `${global.tradeCountTarget} 笔` : '关',
    ],
    [
      '每日盈利金额',
      effective.profitEnabled ? `≥ ${fmtMoney(effective.profitTarget)}` : '关',
      global.profitEnabled ? `≥ ${fmtMoney(global.profitTarget)}` : '关',
    ],
    [
      '每日亏损上限',
      effective.lossEnabled ? `≤ ${fmtMoney(effective.lossCap)}` : '关',
      global.lossEnabled ? `≤ ${fmtMoney(global.lossCap)}` : '关',
    ],
  ];
  return (
    <div className="rounded-md bg-bg-3 p-3">
      <div className="grid grid-cols-[1fr_auto_auto] gap-x-6 gap-y-1.5 text-sm">
        <div className="text-xs text-ink-3">维度</div>
        <div className="text-xs text-ink-3 text-right">生效</div>
        <div className="text-xs text-ink-3 text-right">全局默认</div>
        {rows.map(([label, eff, glob]) => (
          <div key={label} className="contents">
            <div className="text-ink-2">{label}</div>
            <div className="num text-right text-ink-1">{eff}</div>
            <div className="num text-right text-ink-3">{glob}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function describeConfig(c: AC): string {
  if (!c.enabled) return '考核已关闭';
  const parts: string[] = [];
  if (c.tradeCountEnabled) parts.push(`笔数 ≥ ${c.tradeCountTarget}`);
  if (c.profitEnabled) parts.push(`盈利 ≥ ${fmtMoney(c.profitTarget)}`);
  if (c.lossEnabled) parts.push(`亏损 ≤ ${fmtMoney(c.lossCap)}`);
  return parts.length ? parts.join(' · ') : '未启用任何维度';
}
